import React, {Fragment, useEffect, useRef} from 'react';

import cs from 'classnames';
import {colors} from 'js-libs/sweetcommons/styles';
import {toInteger} from 'lodash';
import {Link} from 'react-router';
import styled, {keyframes} from 'styled-components';
import uniqolor from 'uniqolor';

import {UserAvatar} from 'app/components/general/avatars';
import {getUserTitle} from 'app/components/general/avatars/BaseUserAvatar';
import ElapsedTime from 'app/components/general/ElapsedTime';
import {LazyTooltip} from 'app/components/general/LazyTooltip';
import {StyledWithMentionsDiv} from 'app/components/general/Mentionable';
import {UserContentImage} from 'app/general/structures/UserContentContainer/nodeProcessors/MakeImagesZoomable/UserContentImage';
import {
  HIGHLIGHT_HOVERED_CLASS,
  TEXT_HIGHLIGHT_CLASS_PREFIX,
} from 'app/general/structures/UserContentContainer/utils/highlightUtils';
import {ActionsButton} from 'app/general/widgets/ActionsButton';
import {AddReactionButton} from 'app/reactions/components/AddReactionButton';
import {ReactionsCounterButtons} from 'app/reactions/components/ReactionsCounterButtons';
import {useAccountSettingsState} from 'app/settings/api';
import {scrollToNode} from 'app/utils/browser';
import {COMMENTSTATUS} from 'app/utils/constants';
import {removeMediaPath} from 'app/utils/fineuploader';
import {gt} from 'app/utils/i18n';
import {markAsSafe} from 'app/utils/strings';

import {useDeleteComment, useUpdateConversation} from '../api';
import {useTextHighlights} from '../TextHighlightsContextProvider';

import type {ReactNode} from 'react';
import type {
  CommentConversationSerializedType,
  CommentSerializedType,
} from 'sweet-types';

interface Props {
  isTarget: boolean;
  comment: CommentSerializedType;
  canDelete: boolean;
  children?: ReactNode;
}

function HighlightableComment({
  isTarget,
  comment,
  canDelete,
  conversation,
}: Props & {conversation: CommentConversationSerializedType}) {
  const {
    addHighlight,
    clearHighlight,
    hoveredConversationId,
    setHoveredConversationId,
  } = useTextHighlights();

  const conversationId = conversation.id.toString();

  useEffect(() => {
    if (hoveredConversationId === conversationId) {
      scrollToNode(`#comment${comment.id}`);
    }
  }, [hoveredConversationId]);

  useEffect(() => {
    addHighlight(
      conversationId,
      conversation.highlight_start,
      conversation.highlight_end,
      toInteger(comment.object_pk),
      comment.content_type,
      conversation.highlight_text,
      conversation.step_id,
      conversation.highlight_color
    );

    return () => {
      handleMouseOut();
      clearHighlight(conversationId);
    };
  }, []);

  const handleMouseOver = () => {
    setHoveredConversationId(conversationId);
  };

  const handleMouseOut = () => {
    setHoveredConversationId(null);
  };

  const isHighlightedClass = {};
  isHighlightedClass[`${HIGHLIGHT_HOVERED_CLASS}`] =
    hoveredConversationId === conversationId;

  return (
    <div
      className={cs(
        `${TEXT_HIGHLIGHT_CLASS_PREFIX}${conversationId}`,
        isHighlightedClass
      )}
      onMouseOver={handleMouseOver}
      onMouseOut={handleMouseOut}
    >
      <CommentItem
        isTarget={isTarget}
        comment={comment}
        canDelete={canDelete}
      />
    </div>
  );
}

export function CommentItemWithHighlight({
  isTarget,
  comment,
  canDelete,
}: Props) {
  const conversation = comment.comment_conversation;

  if (!comment.is_removed && conversation != null) {
    return (
      <HighlightableComment
        isTarget={isTarget}
        comment={comment}
        canDelete={canDelete}
        conversation={conversation}
      />
    );
  }

  return (
    <CommentItem isTarget={isTarget} comment={comment} canDelete={canDelete} />
  );
}

type ConversationStatusProps = {
  conversation: CommentConversationSerializedType;
};

function ConversationStatus({conversation}: ConversationStatusProps) {
  const spanRef = useRef<HTMLInputElement | null>(null);

  if (conversation.status === COMMENTSTATUS.PENDING) {
    return null;
  }

  const iconClassName =
    conversation.status === COMMENTSTATUS.ACCEPT
      ? 'far fa-check-circle text-success'
      : 'far fa-times-circle text-muted';

  const tooltipText =
    conversation.status === COMMENTSTATUS.ACCEPT
      ? gt('Changes made to address this comment')
      : gt('No changes needed for the highlighted text');

  return (
    <LazyTooltip tooltip={tooltipText}>
      <span ref={spanRef}>
        <i className={iconClassName}></i>
      </span>
    </LazyTooltip>
  );
}

export function CommentItem({
  isTarget,
  comment,
  canDelete,
  children,
}: Props): JSX.Element {
  const [updateConversation] = useUpdateConversation();
  const [deleteComment] = useDeleteComment();

  const {user, id, submit_date, is_removed, reactions} = comment;

  const settings = useAccountSettingsState().data!;

  const userName = getUserTitle(user);

  function getCommentActions() {
    return [
      {
        text: gt('Delete comment'),
        icon: 'far fa-trash',
        key: 'delete',
        callback: () => deleteComment({url: comment.url}),
        condition: () => canDelete && !comment.is_removed,
      },
      {
        text: gt('Changes applied'),
        icon: 'far fa-check-circle',
        key: 'changes-applied',
        callback: () =>
          updateConversation({
            url: comment.comment_conversation!.url,
            status: COMMENTSTATUS.ACCEPT,
          }),
        condition: () =>
          canDelete &&
          !comment.is_removed &&
          comment.comment_conversation != null &&
          comment.comment_conversation.status !== COMMENTSTATUS.ACCEPT,
      },
      {
        text: gt('Changes rejected'),
        icon: 'far fa-times',
        key: 'changes-rejected',
        callback: () =>
          updateConversation({
            url: comment.comment_conversation!.url,
            status: COMMENTSTATUS.REJECTED,
          }),
        condition: () =>
          canDelete &&
          !comment.is_removed &&
          comment.comment_conversation != null &&
          comment.comment_conversation.status !== COMMENTSTATUS.REJECTED,
      },
    ];
  }
  return (
    <StyledHighlightedLI
      id={`comment${id}`}
      className={cs('p-2 pb-1 border-bottom', {
        deleted: is_removed,
        highlighted: isTarget,
      })}
      highlightColor={
        uniqolor(userName, {
          saturation: [35, 55],
          lightness: [88, 96],
        }).color
      }
    >
      {is_removed ? (
        <div className="media">
          <div style={{width: '2.2em'}} className="mr-2" />
          <div className="media-body">
            <StyledDeletedComment className="small d-flex">
              <i className="fa fa-ban" /> {gt('Comment deleted')}
            </StyledDeletedComment>
          </div>
        </div>
      ) : (
        <Fragment>
          <div className="d-flex align-items-start justify-content-between">
            <UserAvatar className="mr-2" user={user} showCard />
            <div className="d-flex flex-column flex-grow-1">
              <div className="d-flex justify-content-between user-name">
                <div className="d-flex">
                  <div className="font-weight-bold mr-2">{userName}</div>
                  {comment.comment_conversation && (
                    <ConversationStatus
                      conversation={comment.comment_conversation}
                    />
                  )}
                </div>
                <div className="comment-actions">
                  <ActionsButton actions={getCommentActions()}>
                    {(buttonProps) => (
                      <button
                        {...buttonProps}
                        className="btn btn-link btn-sm options-menu py-0 px-1 text-muted"
                      />
                    )}
                  </ActionsButton>
                </div>
              </div>
              {children}

              <StyledWithMentionsDiv
                className="message small"
                dangerouslySetInnerHTML={markAsSafe(comment.comment)}
              />
              {comment.attachments.images.map((attachment, index) => (
                <UserContentImage
                  key={index}
                  src={removeMediaPath(
                    attachment.image_ref,
                    settings.MEDIA_URL
                  )}
                  className="mt-2"
                  insideLink={false}
                  mediaURL={settings.MEDIA_URL}
                  mediaURLRead={settings.MEDIA_URL_READ}
                  imageSize="xsmall"
                />
              ))}
              <div className="d-flex justify-content-between align-items-center">
                <ReactionsCounterButtons
                  comment={comment}
                  reactions={reactions}
                />

                <div className="d-flex align-items-center">
                  <AddReactionButton reactions={reactions} comment={comment} />
                  <Link
                    className="d-flex text-muted font-italic"
                    to={comment.html_url}
                  >
                    <small>
                      <ElapsedTime
                        timestamp={submit_date}
                        tooltipPlacement="top"
                        ContainerTag="span"
                        small={true}
                      />
                    </small>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </Fragment>
      )}
    </StyledHighlightedLI>
  );
}

const StyledDeletedComment = styled.div`
  color: ${colors.text};

  i {
    margin-top: 4px;
    margin-right: 3px;
  }
`;
const fadeInKeyframe = keyframes`
    from {
        opacity: 0;
        transform: translateX(-10px);
    }
    to {
        opacity: 1;
        transform: translateX(-0px);
    }
`;

const bgColorKeyframe = keyframes`
  0% {
    background-color: transparent;
  }
  50% {
    background-color: var(--highlight-color);
  }
  100% {
    background-color: transparent;
  }
`;

// Then in your styled component
const StyledHighlightedLI = styled.li<{highlightColor: string}>`
  --highlight-color: ${(props) => props.highlightColor || colors.blueGray};

  &.highlighted {
    animation: ${fadeInKeyframe} 3s ease, ${bgColorKeyframe} 3s ease 1;
    animation-fill-mode: forwards;
  }

  &.deleted {
    opacity: 0.7;
  }

  transition: opacity 250ms;

  .media-body {
    overflow: auto;
  }

  .comment-actions {
    margin-top: -2px;
    margin-left: 1px;
  }

  .user-name {
    font-size: 80%;
  }

  .message {
    word-wrap: break-word;
    word-break: break-word;

    a {
      word-wrap: anywhere;
    }
  }
`;
